import axios from "axios";

import { API_URL, AWS_REGION, ESV_PRIV_BUCKET } from "../utils/Constant";

/** ESV Reports bucket used for invoice PDFs */
const ESV_REPORTS_BUCKET = ESV_PRIV_BUCKET;

const isCanceled = (error) => (
  axios.isCancel?.(error)
  || error?.name === "CanceledError"
  || error?.code === "ERR_CANCELED"
);

/**
 * List invoices.
 * GET /api/usage/invoices/
 * Interceptor injects user_payload + company_name.
 *
 * @param {{
 *   type?: string,
 *   paymentStatus?: 'due'|'paid',
 *   dateFrom?: string,
 *   dateTo?: string,
 *   search?: string,
 *   createdBy?: string,
 *   page?: number,
 *   pageSize?: number,
 *   signal?: AbortSignal,
 * }} params
 */
export const listInvoices = async ({
  type,
  paymentStatus,
  dateFrom,
  dateTo,
  search,
  createdBy,
  page = 1,
  pageSize = 20,
  signal,
} = {}) => {
  try {
    const query = {
      page,
      page_size: Math.min(Math.max(Number(pageSize), 1), 100),
    };

    if (type) query.type = type;
    if (paymentStatus) query.payment_status = paymentStatus;
    if (dateFrom) query.date_from = dateFrom;
    if (dateTo) query.date_to = dateTo;
    if (search) query.search = search;
    if (createdBy) query.created_by = createdBy;

    const response = await axios.get(`${API_URL}/usage/invoices/`, {
      params: query,
      signal,
    });

    const { message, result } = response.data;

    if (message !== "Success") {
      return {
        success: false,
        message,
      };
    }

    return {
      success: true,
      count: result?.count ?? 0,
      invoices: Array.isArray(result?.invoices) ? result.invoices : [],
    };
  } catch (error) {
    if (isCanceled(error)) {
      return { success: false, canceled: true };
    }

    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

/**
 * Names for the Created by filter. Exact-match values for invoices `created_by`.
 * GET /api/usage/invoice-created-by/
 * Interceptor injects user_payload + company_name.
 * Result is already sorted A–Z: { created_by: string[] }.
 *
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, names?: string[], message?: string, canceled?: boolean }>}
 */
export const listInvoiceCreatedBy = async (signal) => {
  try {
    const response = await axios.get(`${API_URL}/usage/invoice-created-by/`, { signal });
    const { message, result } = response.data;

    if (message !== "Success") {
      return {
        success: false,
        message,
      };
    }

    return {
      success: true,
      names: result.created_by,
    };
  } catch (error) {
    if (isCanceled(error)) {
      return { success: false, canceled: true };
    }

    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

/**
 * Testing-complete submissions for the custom invoice picker.
 * GET /api/usage/invoice-submissions/
 * Interceptor injects user_payload + company_name.
 *
 * @param {{
 *   dateFrom?: string,
 *   dateTo?: string,
 *   submittedBy?: string,
 *   search?: string,
 *   page?: number,
 *   pageSize?: number,
 *   signal?: AbortSignal,
 * }} params
 */
export const listInvoiceSubmissions = async ({
  dateFrom,
  dateTo,
  submittedBy,
  search,
  page = 1,
  pageSize = 25,
  signal,
} = {}) => {
  try {
    const query = {
      page,
      page_size: Math.min(Math.max(Number(pageSize), 1), 100),
    };

    if (dateFrom) query.date_from = dateFrom;
    if (dateTo) query.date_to = dateTo;
    if (submittedBy) query.submitted_by = submittedBy;
    if (search) query.search = search;

    const response = await axios.get(`${API_URL}/usage/invoice-submissions/`, {
      params: query,
      signal,
    });

    const { message, result } = response.data;

    if (message !== "Success") {
      return {
        success: false,
        message,
      };
    }

    return {
      success: true,
      count: result.count,
      submissions: result.submissions,
    };
  } catch (error) {
    if (isCanceled(error)) {
      return { success: false, canceled: true };
    }

    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

/**
 * Names for the Submitted by filter on the submission picker.
 * GET /api/usage/invoice-submission-submitted-by/
 * Interceptor injects user_payload + company_name.
 * Result is already sorted A–Z: { submitted_by: string[] }.
 *
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, names?: string[], message?: string, canceled?: boolean }>}
 */
export const listInvoiceSubmissionSubmittedBy = async (signal) => {
  try {
    const response = await axios.get(`${API_URL}/usage/invoice-submission-submitted-by/`, { signal });
    const { message, result } = response.data;

    if (message !== "Success") {
      return {
        success: false,
        message,
      };
    }

    return {
      success: true,
      names: result.submitted_by,
    };
  } catch (error) {
    if (isCanceled(error)) {
      return { success: false, canceled: true };
    }

    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

/**
 * Get a temporary GET URL for an invoice PDF.
 * POST /api/s3_presigned_url/ with ESV Reports bucket.
 * Interceptor injects user_payload + company_name.
 *
 * @param {string} pdfPath S3 key from invoice.pdf_path
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, url?: string, message?: string, canceled?: boolean }>}
 */
export const getInvoicePdfPresignedUrl = async (pdfPath, signal) => {
  try {
    const response = await axios.post(
      `${API_URL}/s3_presigned_url/`,
      {
        action: "get",
        aws_s3_urls: [
          {
            bucket: ESV_REPORTS_BUCKET,
            path: pdfPath,
            region: AWS_REGION,
          },
        ],
      },
      signal ? { signal } : undefined,
    );

    const url = response.data?.result?.[pdfPath];

    if (!url) {
      return {
        success: false,
        message: response.data?.message,
      };
    }

    return {
      success: true,
      url,
    };
  } catch (error) {
    if (isCanceled(error)) {
      return { success: false, canceled: true };
    }

    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

const isSuccessMessage = (message) => String(message || "").startsWith("Success");

const decodePdfBase64 = (value, contentType) => {
  const binary = window.atob(String(value).replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], contentType ? { type: contentType } : undefined);
};

/**
 * Generate a submission invoice PDF.
 * POST /api/usage/invoice-preview/
 * Interceptor injects user_payload + company_name.
 *
 * @param {{
 *   submissionIds: string[],
 *   invoiceDate: string,
 *   amount: string,
 *   clientName: string,
 *   address1: string,
 *   address2?: string,
 *   city: string,
 *   state: string,
 *   postalCode: string,
 *   country: string,
 *   clientEmail: string,
 *   headerFile?: string,
 *   uploadToS3?: boolean,
 * }} payload
 * When uploadToS3 is true the PDF is stored and pdf_base64 is null.
 * The S3 key to save is result.uploaded_to_s3_key.
 */
export const previewSubmissionInvoice = async (payload) => {
  try {
    const body = {
      submission_ids: payload.submissionIds,
      invoice_date: payload.invoiceDate,
      amount: payload.amount,
      client_name: payload.clientName,
      address_1: payload.address1,
      city: payload.city,
      state: payload.state,
      postal_code: payload.postalCode,
      country: payload.country,
      client_email: payload.clientEmail,
    };

    if (payload.address2) body.address_2 = payload.address2;
    if (payload.headerFile) body.header_file = payload.headerFile;
    if (payload.uploadToS3) body.upload_to_s3 = true;

    const response = await axios.post(`${API_URL}/usage/invoice-preview/`, body);
    const { message, result } = response.data;

    if (!isSuccessMessage(message)) {
      return { success: false, message };
    }

    if (payload.uploadToS3) {
      const pdfPath = result?.uploaded_to_s3_key;
      if (!pdfPath) {
        return { success: false, message };
      }

      return {
        success: true,
        filename: result.filename,
        pdfPath,
      };
    }

    if (!result?.pdf_base64) {
      return { success: false, message };
    }

    return {
      success: true,
      blob: decodePdfBase64(result.pdf_base64, result.content_type),
      filename: result.filename,
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

/**
 * Save a submission invoice.
 * POST /api/usage/invoice-save/
 * pdf_path is uploaded_to_s3_key from the preview call with upload_to_s3 true.
 * Interceptor injects user_payload + company_name.
 *
 * @param {{
 *   pdfPath: string,
 *   amount: string,
 *   invoiceDate: string,
 *   createdBy: string,
 *   clientName: string,
 *   invoiceName?: string,
 * }} payload
 */
export const saveSubmissionInvoice = async (payload) => {
  try {
    const body = {
      pdf_path: payload.pdfPath,
      amount: payload.amount,
      invoice_date: payload.invoiceDate,
      created_by: payload.createdBy,
      client_name: payload.clientName,
    };

    if (payload.invoiceName) body.invoice_name = payload.invoiceName;

    const response = await axios.post(`${API_URL}/usage/invoice-save/`, body);
    const { message, result } = response.data;

    if (!isSuccessMessage(message)) {
      return { success: false, message };
    }

    return { success: true, invoice: result };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

/**
 * List saved invoice headers. Default header is first.
 * GET /api/usage/invoice-headers/
 * Interceptor injects user_payload + company_name.
 *
 * @param {AbortSignal} [signal]
 */
export const listInvoiceHeaders = async (signal) => {
  try {
    const response = await axios.get(`${API_URL}/usage/invoice-headers/`, { signal });
    const { message, result } = response.data;

    if (!isSuccessMessage(message)) {
      return { success: false, message };
    }

    return {
      success: true,
      headers: Array.isArray(result) ? result : [],
    };
  } catch (error) {
    if (isCanceled(error)) {
      return { success: false, canceled: true };
    }

    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

/**
 * Create an invoice header from an uploaded S3 key.
 * POST /api/usage/invoice-headers/
 * Interceptor injects user_payload + company_name.
 *
 * @param {{ name: string, imageUrl: string, isDefault?: boolean }} payload
 */
export const createInvoiceHeader = async ({ name, imageUrl, isDefault = false }) => {
  try {
    const response = await axios.post(`${API_URL}/usage/invoice-headers/`, {
      name,
      image_url: imageUrl,
      is_default: isDefault,
    });
    const { message, result } = response.data;

    if (!isSuccessMessage(message)) {
      return { success: false, message };
    }

    return { success: true, header: result };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

/**
 * Edit an invoice header. is_default is always sent.
 * PATCH /api/usage/invoice-headers/ with action "edit".
 * Interceptor injects user_payload + company_name.
 *
 * @param {{ headerId: string, name: string, imageUrl: string, isDefault: boolean }} payload
 */
export const editInvoiceHeader = async ({
  headerId, name, imageUrl, isDefault,
}) => {
  try {
    const response = await axios.patch(`${API_URL}/usage/invoice-headers/`, {
      action: "edit",
      header_id: headerId,
      name,
      image_url: imageUrl,
      is_default: isDefault,
    });
    const { message, result } = response.data;

    if (!isSuccessMessage(message)) {
      return { success: false, message };
    }

    return { success: true, header: result };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};

/**
 * Delete an invoice header.
 * PATCH /api/usage/invoice-headers/ with action "delete".
 * Interceptor injects user_payload + company_name.
 *
 * @param {string} headerId
 */
export const deleteInvoiceHeader = async (headerId) => {
  try {
    const response = await axios.patch(`${API_URL}/usage/invoice-headers/`, {
      action: "delete",
      header_id: headerId,
    });
    const { message } = response.data;

    if (!isSuccessMessage(message)) {
      return { success: false, message };
    }

    return { success: true, message };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message,
    };
  }
};
